/**
 * Mobile App Configuration Registry
 *
 * Centralized configuration for mobile apps used in the platform.
 * Currently supports SteadyStride. (SmartDiary entry dropped 2026-06-29 — app
 * is descoped; remove all surface references.)
 *
 * Usage:
 *   import { getAppConfig, getDeepLink, DEFAULT_APP, STORE_BADGES } from '../constants/mobileApps';
 *
 *   const config = getAppConfig('steadystride');
 *   const deepLink = getDeepLink('steadystride', 'ABC-123');
 */

/**
 * Standardized App Store badge image URLs
 * Use these consistently across all components
 */
export const STORE_BADGES = {
    appStore: 'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg',
    /* Local copy of Google's official badge with its transparent margin trimmed.
       The asset ships with 41px of padding top and bottom, so at any shared
       height it rendered a third smaller than Apple's. Artwork is unmodified —
       only the empty margin is gone — which lets both badges use one height
       instead of being compensated for with magic numbers. */
    googlePlay: '/brand/google-play-badge.png'
};

export const MOBILE_APPS = {
    steadystride: {
        id: 'steadystride',
        name: 'SteadyStride',
        tagline: 'Your Clinical Movement Companion',
        description: 'Track your rehabilitation progress with clinical-grade movement analysis and clinician-supported guidance.',
        shortDescription: 'Connect your wearable, build a picture of how you move, and share it with a qualified physiotherapist when you want guidance.',

        // App Store URLs
        appStoreUrl: 'https://apps.apple.com/gb/app/steadystride/id6758022129',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.physioactivity.steadystride',

        // Deep link configuration
        deepLinkBase: 'https://connect.physioactivity.com/steadystride/code',

        // Static assets
        qrCodePath: '/qr-codes/steadystride-qr.png',
        redirectPage: '/steadystride-redirect.html',

        // Website route
        pageUrl: '/steadystride',

        // Target audience
        audience: 'clinical', // clinical | general
    }
    // smartdiary entry removed 2026-06-29 — app descoped, all surface
    // references stripped (Physios service tier, AppRouter /smartdiary
    // redirect, JSDoc examples in AccessCodeDisplay + AppDownloadSection).
};

/**
 * Default app to use when none is specified
 * Currently SteadyStride for clinical focus
 */
export const DEFAULT_APP = 'steadystride';

/**
 * Get app configuration by ID
 * Falls back to default app if ID not found
 *
 * @param {string} appId - App identifier ('steadystride')
 * @returns {object} App configuration object
 */
export function getAppConfig(appId) {
    return MOBILE_APPS[appId] || MOBILE_APPS[DEFAULT_APP];
}

/**
 * Generate deep link URL for a given app and access code
 *
 * @param {string} appId - App identifier
 * @param {string} code - Access code (e.g., 'ABC-123')
 * @returns {string} Full deep link URL
 *
 * @example
 * getDeepLink('steadystride', 'ABC-123')
 * // Returns: 'https://connect.physioactivity.com/steadystride/code/ABC-123'
 */
export function getDeepLink(appId, code) {
    const config = getAppConfig(appId);
    return `${config.deepLinkBase}/${code}`;
}

/**
 * Get list of all available app IDs
 * @returns {string[]} Array of app IDs
 */
export function getAvailableApps() {
    return Object.keys(MOBILE_APPS);
}

/**
 * Check if an app ID is valid
 * @param {string} appId - App identifier to check
 * @returns {boolean} True if valid app ID
 */
export function isValidAppId(appId) {
    return appId in MOBILE_APPS;
}

/**
 * Web portal URL for advisor/clinician login on desktop and tablet.
 */
export const PORTAL_URL = 'https://portal.physioactivity.com';

/**
 * Platform-aware login/register redirect.
 * - Tablet / desktop → web portal
 * - iPhone / iPod → App Store for the given app
 * - Android phone → Play Store for the given app
 *
 * @param {string} appId - App identifier (defaults to SteadyStride)
 */
export function redirectToLogin(appId = DEFAULT_APP) {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isTablet = /iPad/.test(userAgent) || (/android/i.test(userAgent) && !/mobile/i.test(userAgent));
    const config = getAppConfig(appId);

    if (isTablet) {
        window.location.href = PORTAL_URL;
    } else if (/iPhone|iPod/.test(userAgent) && !window.MSStream) {
        window.location.href = config.appStoreUrl;
    } else if (/android/i.test(userAgent)) {
        window.location.href = config.playStoreUrl;
    } else {
        window.location.href = PORTAL_URL;
    }
}
